import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import { Checkbox } from '../../../components/ui/Checkbox';

const RareMobsTracker = ({ rareMobs, onToggleMob }) => {
  const [expandedZones, setExpandedZones] = useState({});

  // Group rare mobs by zone
  const mobsByZone = rareMobs?.reduce((acc, mob) => {
    if (!acc?.[mob?.zone]) {
      acc[mob.zone] = [];
    }
    acc?.[mob?.zone]?.push(mob);
    return acc;
  }, {});

  const toggleZone = (zone) => {
    setExpandedZones(prev => ({
      ...prev,
      [zone]: !prev?.[zone]
    }));
  };

  const getZoneStats = (zoneMobs) => {
    const defeated = zoneMobs?.filter(mob => mob?.defeated)?.length || 0;
    const total = zoneMobs?.length || 0;
    const percentage = total > 0 ? Math.round((defeated / total) * 100) : 0;
    return { defeated, total, percentage };
  };

  const getOverallStats = () => {
    const defeated = rareMobs?.filter(mob => mob?.defeated)?.length || 0;
    const total = rareMobs?.length || 0;
    const percentage = total > 0 ? Math.round((defeated / total) * 100) : 0;
    return { defeated, total, percentage };
  };

  const getStatusColor = (percentage) => {
    if (percentage === 100) return 'var(--color-success)';
    if (percentage >= 50) return 'var(--color-warning)';
    return 'var(--color-error)';
  };

  const overallStats = getOverallStats();

  return (
    <div className="bg-card rounded-xl p-4 md:p-6 lg:p-8 border border-border">
      <div className="flex items-center justify-between mb-4 md:mb-6">
        <div>
          <h2 className="text-xl md:text-2xl lg:text-3xl font-semibold mb-1">
            Rare Mobs Tracker
          </h2>
          <p className="text-xs md:text-sm text-muted-foreground">
            Track defeated rare mobs across the Buried Sea
          </p>
        </div>
        <Icon name="Skull" size={24} color="var(--color-primary)" />
      </div>
      {/* Overall Progress */}
      <div className="mb-6 p-4 rounded-lg bg-muted/50">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm md:text-base font-medium">
            Overall Progress
          </span>
          <span className="text-sm md:text-base font-semibold">
            {overallStats?.defeated} / {overallStats?.total}
          </span>
        </div>
        <div className="w-full h-2 md:h-2.5 bg-muted rounded-full overflow-hidden">
          <div 
            className="h-full transition-all duration-500"
            style={{ 
              width: `${overallStats?.percentage}%`,
              backgroundColor: getStatusColor(overallStats?.percentage)
            }}
          />
        </div>
      </div>
      {/* Zones with Rare Mobs */}
      <div className="space-y-3 md:space-y-4">
        {Object.entries(mobsByZone || {})?.map(([zone, zoneMobs]) => {
          const zoneStats = getZoneStats(zoneMobs);
          const isExpanded = expandedZones?.[zone];

          return (
            <div key={zone} className="border border-border rounded-lg overflow-hidden">
              {/* Zone Header */}
              <button
                onClick={() => toggleZone(zone)}
                className="w-full px-4 py-3 bg-muted/30 hover:bg-muted/50 transition-colors flex items-center justify-between"
              >
                <div className="flex items-center gap-3">
                  <Icon 
                    name={isExpanded ? 'ChevronDown' : 'ChevronRight'} 
                    size={18}
                    color="var(--color-muted-foreground)"
                  />
                  <div className="text-left">
                    <h3 className="text-sm md:text-base font-semibold">
                      {zone}
                    </h3>
                    <p className="text-xs text-muted-foreground">
                      {zoneStats?.defeated} of {zoneStats?.total} defeated
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-sm font-semibold">
                    {zoneStats?.percentage}%
                  </span>
                  <div className="w-20 h-1.5 bg-muted rounded-full overflow-hidden hidden sm:block">
                    <div 
                      className="h-full transition-all duration-500"
                      style={{ 
                        width: `${zoneStats?.percentage}%`,
                        backgroundColor: getStatusColor(zoneStats?.percentage)
                      }}
                    />
                  </div>
                </div>
              </button>
              {/* Zone Mobs List */}
              {isExpanded && (
                <div className="p-4 space-y-2">
                  {zoneMobs?.map((mob) => (
                    <div 
                      key={mob?.id}
                      className="flex items-start gap-3 p-3 rounded-lg hover:bg-muted/30 transition-colors"
                    >
                      <Checkbox
                        checked={mob?.defeated}
                        onChange={() => onToggleMob?.(mob?.id)}
                        id={`mob-${mob?.id}`}
                      />
                      <div className="flex-1">
                        <label 
                          htmlFor={`mob-${mob?.id}`}
                          className="text-sm md:text-base font-medium cursor-pointer"
                        >
                          {mob?.name}
                        </label>
                        {mob?.location && (
                          <p className="text-xs text-muted-foreground mt-0.5">
                            {mob?.location}
                          </p>
                        )}
                        {mob?.defeated && mob?.lastDefeated && (
                          <p className="text-xs text-success mt-1">
                            Defeated: {new Date(mob.lastDefeated)?.toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric'
                            })}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default RareMobsTracker;